import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import API_URL from "../config/config";
import { useNavigate } from 'react-router-dom';

const ResetPassword = () => {
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const mobileValue = watch('mobile', '');


  const apiUrl = API_URL;

  const cleanMobileNumber = (mobile) => mobile.replace(/\D/g, '');
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 10) return mobileValue;
    if (digits.length > 5) {
      return `${digits.slice(0, 5)} ${digits.slice(5, 10)}`;
    }
    return digits;
  };
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('mobile', formatted, { shouldValidate: true });
  };
  const formatMobileForDB = (mobile) => {
    const digits = cleanMobileNumber(mobile);
    if (digits.length === 10) {
      return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return `+91 ${digits}`; // fallback if incomplete
  };

  const sendOtp = async (data) => {
    const mobile = cleanMobileNumber(data.mobile);
    const dbMobile = formatMobileForDB(data.mobile);
    console.log('ResetPassword: Sending OTP for mobile:', mobile, 'DB format:', dbMobile);

    if (mobile.length !== 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Verify user exists
      const validityResponse = await axios.post(
        `${apiUrl}/user/get-validity`,
        { mobileno: dbMobile },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
      );
      console.log('ResetPassword: Validity response:', validityResponse.data);
      if (validityResponse.status !== 200 || validityResponse.data.error) {
        toast.error('User not found');
        return;
      }

      // Step 2: Send OTP
      const otpResponse = await axios.post(
        `${apiUrl}/otp/send-otp`,
        { mobile },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
      );
      console.log('ResetPassword: OTP response:', otpResponse.data);
      if (otpResponse.status === 200 && otpResponse.data.success) {
        setOtpSent(true);
        toast.success('OTP Sent Successfully');
      } else {
        toast.error('Failed to send OTP');
      }
    } catch (error) {
      console.error('ResetPassword: Error sending OTP:', error);
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (data) => {
    const mobile = cleanMobileNumber(data.mobile);
    const otp = data.otp.trim();
    console.log('ResetPassword: Verifying OTP for mobile:', mobile, 'OTP:', otp);

    if (mobile.length !== 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${apiUrl}/otp/verify-otp`,
        { mobile, otp },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
      );
      console.log('ResetPassword: Verify OTP response:', response.data);
      if (response.status === 200 && response.data.success) {
        setOtpVerified(true);
        toast.success('OTP Verified');
      } else {
        toast.error('OTP Verification Failed');
      }
    } catch (error) {
      console.error('ResetPassword: Error verifying OTP:', error);
      toast.error(error.response?.data?.message || 'OTP Verification Failed');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (data) => {
    const mobile = cleanMobileNumber(data.mobile);
    const dbMobile = formatMobileForDB(data.mobile);
    const { newPassword, confirmPassword } = data;
    console.log('ResetPassword: Resetting password for mobile:', dbMobile);

    if (mobile.length !== 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.patch(
        `${apiUrl}/user/update-password`,
        { mobileno: dbMobile, password: newPassword },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
      );
      console.log('ResetPassword: Reset password response:', response.data);
      if (response.status === 200 && !response.data.error) {
        toast.success('Password Reset Successfully');
        navigate('/');
      } else {
        toast.error(response.data.message || 'Reset failed');
      }
    } catch (error) {
      console.error('ResetPassword: Error resetting password:', error);
      toast.error(error.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center font-Poppins">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Reset Password</h2>
        <form className="space-y-6">
          {/* Mobile Number */}
          <div>
            <label className="text-sm font-bold text-gray-800">Mobile Number</label>
            <div className="relative mt-1 flex items-center">
              <span className="inline-block px-3 py-2 bg-gray-100 border border-r-0 rounded-l-lg">+91</span>
              <input
                type="text"
                {...register('mobile', {
                  required: 'Mobile number is required',
                  pattern: {
                    value: /^\d{5}\s\d{5}$/,
                    message: 'Invalid phone number format (e.g., 66666 66667)',
                  },
                })}
                onChange={handlePhoneChange}
                placeholder="66666 66667"
                maxLength={11}
                className={`w-full p-2 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-[#FFC107] ${errors.mobile ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
            </div>
            {errors.mobile && (
              <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>
            )}
          </div>


          {/* OTP */}
          {otpSent && (
            <div>
              <label className="text-sm font-bold text-gray-800">OTP</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                <input
                  {...register('otp', { required: 'OTP is required' })}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFC107] focus:border-[#FFC107] ${errors.otp ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter OTP"
                  type="text"
                  maxLength={6}
                />
                {errors.otp && (
                  <p className="text-red-500 text-xs mt-1">{errors.otp.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Password Fields */}
          {otpVerified && (
            <>
              <div>
                <label className="text-sm font-bold text-gray-800">New Password</label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    {...register('newPassword', {
                      required: 'New password is required',
                      minLength: { value: 6, message: 'Password must be at least 6 characters' },
                    })}
                    className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFC107] focus:border-[#FFC107] ${errors.newPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter New Password"
                    type={showNewPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  {errors.newPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.newPassword.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-gray-800">Confirm Password</label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    {...register('confirmPassword', {
                      required: 'Confirm password is required',
                      validate: (value) =>
                        value === watch('newPassword') || 'Passwords do not match',
                    })}
                    className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFC107] focus:border-[#FFC107] ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

            </>
          )}

          {/* Buttons */}
          <div className="flex justify-center">
            {loading ? (
              <div className="relative">
                <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-[#FFC107]"></div>
                <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-xs font-medium">
                  Loading...
                </div>
              </div>
            ) : !otpSent ? (
              <button
                type="button"
                onClick={handleSubmit(sendOtp)}
                className="px-6 py-2 bg-gradient-to-r from-[#FFC107] to-[#FFA000] text-white rounded-lg font-semibold hover:from-[#FFA000] hover:to-[#FFC107] transition-all duration-200 shadow-md"
              >
                Send OTP
              </button>
            ) : !otpVerified ? (
              <button
                type="button"
                onClick={handleSubmit(verifyOtp)}
                className="px-6 py-2 bg-gradient-to-r from-[#FFC107] to-[#FFA000] text-white rounded-lg font-semibold hover:from-[#FFA000] hover:to-[#FFC107] transition-all duration-200 shadow-md"
              >
                Verify OTP
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit(resetPassword)}
                className="px-6 py-2 bg-gradient-to-r from-[#FFC107] to-[#FFA000] text-white rounded-lg font-semibold hover:from-[#FFA000] hover:to-[#FFC107] transition-all duration-200 shadow-md"
              >
                Reset Password
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
